import { logger } from '@/utils/logger';
import { watch, computed, type Ref } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { FilterBy } from '@/interfaces/Dashboard';
import { isEqual } from 'lodash';

export function useFilterByManager(filterBy: Ref<FilterBy[] | undefined>) {
  const dashboardStore = useDashboardStore();

  // Create a map for quick filter lookup
  const filtersMap = computed(() => {
    if (!filterBy.value) return new Map<string, FilterBy>();

    const map = new Map<string, FilterBy>();
    filterBy.value.forEach((filter) => {
      if (filter.key) {
        map.set(filter.key, filter);
      }
    });
    return map;
  });

  // Find dependant filters for a given filter key
  const getDependantFilters = (filterKey: string): FilterBy[] => {
    if (!filterBy.value) return [];

    return filterBy.value.filter((filter) => {
      if (Array.isArray(filter.dependentOn)) {
        return filter.dependentOn.includes(filterKey);
      }
      return filter.dependentOn === filterKey;
    });
  };

  // Check if a filter has all parent dependencies satisfied
  const hasAllParentDependenciesSatisfied = (filter: FilterBy): boolean => {
    if (!filter.dependentOn) return true;

    const parentKeys = Array.isArray(filter.dependentOn) ? filter.dependentOn : [filter.dependentOn];

    return parentKeys.every((parentKey) => {
      const parentFilter = filtersMap.value.get(parentKey);
      return hasValidParentOptions(parentFilter);
    });
  };

  // Recursively get all dependant filters in order
  const getAllDependantFilters = (filterKey: string): FilterBy[] => {
    const directDependants = getDependantFilters(filterKey);
    let allDependants: FilterBy[] = [...directDependants];

    directDependants.forEach((dependant) => {
      if (dependant.key) {
        allDependants = [...allDependants, ...getAllDependantFilters(dependant.key)];
      }
    });

    return allDependants;
  };

  // Get filters in proper initialization order (independent first, then dependants)
  const getFiltersInOrder = (): FilterBy[] => {
    if (!filterBy.value) return [];

    const independent = filterBy.value.filter((filter) => !filter.dependentOn);
    const dependent = filterBy.value.filter((filter) => filter.dependentOn);

    // Simple approach: just return independent first, then dependent
    // For complex hierarchies, you might need topological sorting
    return [...independent, ...dependent];
  };

  // Fetch default values for a filter
  const fetchDefaultValues = async (filter: FilterBy): Promise<string[]> => {
    if (!filter.defaultValues) return [];

    try {
      const defaultResult = await dashboardStore.fetchDataSource(filter.defaultValues);
      return defaultResult?.data?.map((d: any) => d.value) ?? [];
    } catch (error) {
      console.error(`Failed to fetch default values for ${filter.label}:`, error);
      return [];
    }
  };

  // Set default 'all' value for empty filters
  const setDefaultAllValue = (filter: FilterBy) => {
    if (filter.value === undefined || filter.value === null || filter.value === '') {
      if (filter.multiselect) {
        filter.value = ['all'];
      } else {
        filter.value = 'all';
      }
    } else if (filter.multiselect && Array.isArray(filter.value) && filter.value.length === 0) {
      filter.value = ['all'];
    }
  };

  // Check if parent filter has valid options
  const hasValidParentOptions = (parentFilter: FilterBy | undefined): boolean => {
    return true; //!!(parentFilter?.values && parentFilter.values.length > 0);
  };

  // Build params for dependent filter with multiple dependencies
  const buildDependentParams = (filter: FilterBy): Record<string, any> => {
    const params: Record<string, any> = {};

    if (!filter.dependentOn) return params;

    const parentKeys = Array.isArray(filter.dependentOn) ? filter.dependentOn : [filter.dependentOn];

    parentKeys.forEach((parentKey) => {
      const parentFilter = filtersMap.value.get(parentKey);
      if (!parentFilter) return;

      if (parentFilter.multiselect && Array.isArray(parentFilter.value)) {
        const hasAll = parentFilter.value.includes('all');
        if (hasAll) {
          // When "All" is selected, send all actual values from parent filter's options
          const allActualValues =
            parentFilter.values?.filter((item) => item.value !== 'all').map((item) => item.value) || [];
          params[parentFilter.key!] = allActualValues;
        } else {
          // Send the selected values (excluding 'all' if present)
          params[parentFilter.key!] = parentFilter.value.filter((v) => v !== 'all');
        }
      } else {
        if (parentFilter.value === 'all') {
          // When "All" is selected, send all actual values from parent filter's options
          const allActualValues =
            parentFilter.values?.filter((item) => item.value !== 'all').map((item) => item.value) || [];
          params[parentFilter.key!] = allActualValues;
        } else {
          params[parentFilter.key!] = parentFilter.value;
        }
      }
    });

    logger.log(`🔄 Built params for ${filter.label}:`, params);

    return params;
  };

  // Clear filter options and value
  const clearFilterOptions = (filter: FilterBy) => {
    filter.values = [];
    filter.value = filter.multiselect ? [] : '';
    setDefaultAllValue(filter);
  };

  // Fetch options for a filter
  const fetchFilterOptions = async (filter: FilterBy): Promise<boolean> => {
    logger.log(`🔍 fetchFilterOptions called for: ${filter.label}`);

    if (!filter.dataSource) {
      logger.log(`⏭️ No dataSource for ${filter.label}, skipping`);
      return true;
    }

    // Check if all parent dependencies are satisfied
    if (filter.dependentOn && !hasAllParentDependenciesSatisfied(filter)) {
      logger.log(`❌ Not all parent dependencies satisfied for ${filter.label}, clearing options`);
      clearFilterOptions(filter);
      return false;
    }

    const params = buildDependentParams(filter);

    try {
      logger.log(`📡 Fetching data for ${filter.label} with params:`, params);
      const { data: newOptions } = await dashboardStore.fetchDataSource(filter.dataSource, { params, skipCache: true });
      logger.log(`📊 Received options for ${filter.label}:`, newOptions?.length);

      // Ensure reactive update by creating a new array
      if (newOptions && newOptions.length > 0) {
        filter.values = [{ label: 'All', value: 'all' }, ...newOptions];
      } else {
        filter.values = [];
      }

      // Set default value if specified and we don't already have a value
      if (filter.defaultValues && !filter.value) {
        const defaults = await fetchDefaultValues(filter);
        if (defaults.length > 0) {
          if (filter.multiselect) {
            filter.value = [...defaults];
          } else {
            filter.value = defaults[0];
          }
        } else {
          setDefaultAllValue(filter);
        }
      } else {
        setDefaultAllValue(filter);
      }

      return true;
    } catch (error) {
      console.error(`❌ Failed to fetch options for ${filter.label}:`, error);
      clearFilterOptions(filter);
      return false;
    }
  };

  // Initialize all filters in proper sequential order
  const initializeFilters = async (): Promise<void> => {
    if (!filterBy.value) return;

    // First, set default 'all' values for all filters
    filterBy.value.forEach(setDefaultAllValue);

    // Get filters in the correct order (independent first, then dependants)
    const filtersInOrder = getFiltersInOrder();

    // Process each filter SEQUENTIALLY (one after another)
    for (const filter of filtersInOrder) {
      if (filter.dataSource) {
        await fetchFilterOptions(filter);
      }

      // Small delay to ensure Vue reactivity updates
      await new Promise((resolve) => setTimeout(resolve, 10));
    }
  };

  // Recursively update dependant filters when a parent changes
  const updateDependantFilters = async (parentFilterKey: string): Promise<void> => {
    const dependants = getAllDependantFilters(parentFilterKey);

    logger.log(
      `📋 Updating dependants for ${parentFilterKey}:`,
      dependants.map((d) => d.label)
    );

    if (dependants.length === 0) {
      logger.log('❌ No dependants found for', parentFilterKey);
      return;
    }

    // Update dependants SEQUENTIALLY
    for (const dependant of dependants) {
      logger.log(`🔄 Processing dependant: ${dependant.label}`);
      const success = await fetchFilterOptions(dependant);
      logger.log(`✅ Finished ${dependant.label}, success: ${success}`);
    }
  };

  // Setup watchers for parent filters
  const setupParentWatchers = (): void => {
    if (!filterBy.value) return;

    filterBy.value.forEach((filter) => {
      if (!filter.key) return;

      const hasDependants = getDependantFilters(filter.key).length > 0;
      if (!hasDependants) return;

      watch(
        () => [filter.value, filter.values],
        async ([newValue, newValues], [oldValue, oldValues]) => {
          const valueChanged = newValue !== oldValue;
          const optionsChanged = !isEqual(newValues, oldValues);

          // Enhanced logging
          logger.log(`=== ${filter.label} Watch Trigger ===`);
          logger.log('valueChanged:', valueChanged, 'optionsChanged:', optionsChanged);
          logger.log('newValue:', newValue, 'oldValue:', oldValue);
          logger.log('newValues ref:', newValues, 'oldValues ref:', oldValues);
          logger.log('newValues length:', newValues?.length, 'oldValues length:', oldValues?.length);
          logger.log('isEqual result:', isEqual(newValues, oldValues));
          logger.log('Reference equality:', newValues === oldValues);

          if (valueChanged || optionsChanged) {
            logger.log(`🚀 Updating dependants for ${filter.label}`);
            await updateDependantFilters(filter.key!);
          } else {
            logger.log(`⏭️ No changes detected, skipping update`);
          }
          logger.log(`\r\n`);
        },
        { deep: true }
      );
    });
  };

  // Refresh a specific filter and its dependants
  const refreshFilter = async (filterKey: string): Promise<void> => {
    const filter = filtersMap.value.get(filterKey);
    if (filter) {
      await fetchFilterOptions(filter);
      await updateDependantFilters(filterKey);
    }
  };

  // Refresh all filters
  const refreshAllFilters = async (): Promise<void> => {
    await initializeFilters();
  };

  // Get current filter state as params for API calls
  const getFilterParams = (): Record<string, any> => {
    if (!filterBy.value) return {};

    const params: Record<string, any> = {};

    filterBy.value.forEach((filter) => {
      if (filter.key && filter.value !== undefined && filter.value !== null && filter.value !== '') {
        if (filter.value === 'all') return;

        if (Array.isArray(filter.value)) {
          const filteredValues = filter.value.filter((v) => v !== 'all');
          if (filteredValues.length > 0) {
            params[filter.key] = filteredValues;
          }
        } else {
          params[filter.key] = filter.value;
        }
      }
    });

    return params;
  };

  // Clear all filter values
  const clearAllFilters = (): void => {
    if (!filterBy.value) return;

    filterBy.value.forEach((filter) => {
      setDefaultAllValue(filter);
    });
  };

  // Initialize everything
  const initializeFilterByManager = async (): Promise<void> => {
    await initializeFilters();
    setupParentWatchers();
  };

  return {
    initializeFilterByManager,
    refreshFilter,
    refreshAllFilters,
    getFilterParams,
    clearAllFilters,
    filtersMap,
    getDependantFilters,
    getAllDependantFilters,
    hasAllParentDependenciesSatisfied, // Export for testing/debugging
  };
}
